/**
 * Agent Runs API Contracts
 *
 * Request/response shapes for BOTH agent-run routers:
 *   - `/internal/agent-runs` — service-key, the ledger's write + read API (U2)
 *   - `/v1/agent-runs`       — Clerk super-admin, read-only (U14, SCH-1219)
 *
 * They share one service layer and therefore one row shape. The row itself
 * lives with the vocabularies in `schemas/entities/agent-run.ts` and is
 * imported, not redefined. The internal contracts come first; the admin ones
 * are grouped under their own heading at the bottom.
 *
 * Plan: docs/_archive/2026-10-17-plans-sweep/plans/2026-08-29-001-feat-agent-run-ledger-event-driven-runs-plan.md
 */

import { z } from 'zod'
import {
  AgentRunEventTypeSchema,
  AgentRunExecutorSchema,
  AgentRunLaneSchema,
  AgentRunModeSchema,
  AgentRunSchema,
  AgentRunSourceSchema,
  AgentRunStatusSchema,
  AgentRunTriggerSchema
} from '../../schemas/entities/agent-run'
// The phase blocks the brief serves are declared with the phase vocabulary once.
import {
  AcceptedEnvelopeSchema,
  AgentPhaseNameSchema,
  PHASE_EVENT_PAYLOAD_MAX_CHARS,
  PhaseNameSchema,
  PhaseStartEventPayloadSchema
} from '../../schemas/entities/agent-run-phases'
// The brief hands the VM its runner and model for this lane and phase (R11);
// the shape is the same config entry the tick resolved, not a parallel one.
import { FactoryPhaseCapsSchema, FactoryRoutingEntrySchema } from '../../schemas/entities/factory-config'
// A run's event trail IS `OperationsEvent` rows joined by `correlationId` —
// there is no child table — so the trail contract imports that wire shape
// rather than redefining a parallel one.
import { OperationsEventSchema } from '../../schemas/entities/operations-event'

// POST /internal/agent-runs

export const CreateAgentRunRequestSchema = z.object({
  lane: AgentRunLaneSchema,
  source: AgentRunSourceSchema,
  sourceRef: z.string().min(1).optional(),
  trigger: AgentRunTriggerSchema,
  mode: AgentRunModeSchema,
  executor: AgentRunExecutorSchema.optional(),
  /**
   * The coordinator's reply-to address for this run (`bridge:session_<id>`).
   * A dispatch FACT, carried at creation rather than as a `dispatched` event —
   * who to report to is state, not something the run reports about itself
   * (SCH-1463 KTD3). It records dispatch INTENT, never acknowledgement.
   *
   * `.max(191)` mirrors the column width and is a DELIBERATE departure from
   * `sourceRef` / `fingerprint` / `linearIssueKey`, which carry `.min(1)` and no
   * maximum: without it an over-length address reaches MySQL and fails at write
   * time as a 500, instead of being refused here as invalid input.
   */
  sessionBridge: z.string().min(1).max(191).optional(),
  fingerprint: z.string().min(1).optional(),
  /** The parent run's PUBLIC id, for an agent-spawned run. */
  parentRunId: z.string().min(1).optional(),
  linearIssueKey: z.string().min(1).optional(),
  /** The header + artifact paths handed to the executor. Loose JSON — the shape is U2's to define. */
  brief: z.record(z.string(), z.unknown()).optional(),
  /** The `WebhookDelivery` public id that caused this run, when one exists. */
  webhookDeliveryId: z.string().min(1).optional(),
  /**
   * The run's spend ceiling in dollars, frozen on the row at create
   * (factory operator plan, U3). The tick's dispatcher writes this column at
   * claim time; a launcher run is claimed through `/claim`, which writes none,
   * so without it the heartbeat's budget stop would have no ceiling to check.
   */
  budgetUsd: z.number().positive().optional()
})
export type CreateAgentRunRequest = z.infer<typeof CreateAgentRunRequestSchema>

export const CreateAgentRunResponseSchema = z.object({
  message: z.string(),
  data: AgentRunSchema
})
export type CreateAgentRunResponse = z.infer<typeof CreateAgentRunResponseSchema>

/**
 * 409 shape when either unique constraint collides with an existing run:
 * `lane` + `fingerprint` (R6 — two recorders of the same meeting resolve to
 * the one run that already exists, not a second one) or `source` + `sourceRef`
 * (a redelivery of the same inbound event resolves to the run it already
 * created).
 */
export const CreateAgentRunConflictResponseSchema = z.object({
  message: z.string(),
  error: z.enum(['DUPLICATE_FINGERPRINT', 'DUPLICATE_SOURCE_REF']),
  /** The existing run's PUBLIC id — the caller's redelivery resolves to this run. */
  existingRunId: z.string()
})
export type CreateAgentRunConflictResponse = z.infer<typeof CreateAgentRunConflictResponseSchema>

// GET /internal/agent-runs

export const ListAgentRunsRequestSchema = z
  .object({
    status: AgentRunStatusSchema.optional(),
    executor: AgentRunExecutorSchema.optional(),
    lane: AgentRunLaneSchema.optional(),
    /** Only runs currently `PARKED` — the `sj runs --parked` view. */
    parked: z.coerce.boolean().optional(),
    /** Only `PARKED` runs whose follow-up has already arrived and is waiting to be routed. */
    resumePending: z.coerce.boolean().optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
    /** Public id of the last run on the previous page. */
    cursor: z.string().min(1).optional()
  })
  .strict()
export type ListAgentRunsRequest = z.infer<typeof ListAgentRunsRequestSchema>

export const ListAgentRunsResponseSchema = z.object({
  message: z.string(),
  data: z.array(AgentRunSchema)
})
export type ListAgentRunsResponse = z.infer<typeof ListAgentRunsResponseSchema>

// GET /internal/agent-runs/:publicId

export const GetAgentRunRequestSchema = z.object({
  publicId: z.string().min(1)
})
export type GetAgentRunRequest = z.infer<typeof GetAgentRunRequestSchema>

export const GetAgentRunResponseSchema = z.object({
  message: z.string(),
  data: AgentRunSchema
})
export type GetAgentRunResponse = z.infer<typeof GetAgentRunResponseSchema>

// POST /internal/agent-runs/:publicId/claim
//
// Guarded QUEUED -> RUNNING transition (service key). Mints the per-run
// report token and returns it in PLAINTEXT exactly once — only
// `reportTokenHash` (sha256) is ever persisted, and no other read surface
// returns the token again. A second claim on an already-claimed run is a 409
// with no new token issued.

export const ClaimAgentRunResponseSchema = z.object({
  message: z.string(),
  data: AgentRunSchema.extend({
    /** Plaintext report token — THE ONLY TIME this value is ever returned. */
    reportToken: z.string()
  })
})
export type ClaimAgentRunResponse = z.infer<typeof ClaimAgentRunResponseSchema>

// POST /internal/agent-runs/:publicId/events
//
// Token-authed (the per-run report token minted by claim), not service-key
// authed — this is the one route an executor with no other credential can
// call.

export const RecordAgentRunEventRequestSchema = z
  .object({
    type: AgentRunEventTypeSchema,
    /** When the event actually occurred, if different from receipt time. ISO-8601. */
    at: z.string().datetime().optional(),
    summary: z.string().optional(),
    /** Structured detail — retro's six headings, a block reason, etc. Shape is per-`type`. */
    data: z.record(z.string(), z.unknown()).optional()
  })
  // The phase runner's two outcome types are NOT free for a run to report.
  // `classified` is written by assessment and the override route only — a run
  // token that could post one could forge the precision baseline. `phase` is
  // accepted here as a START edge only: a done edge moves the phase cursor and
  // must go through phase-complete, in one transaction with it (KTD6, KTD7).
  .superRefine((body, ctx) => {
    if (body.type === 'classified') {
      ctx.addIssue({ code: 'custom', path: ['type'], message: 'classified is not reported by a run' })
    }
    if (body.type !== 'phase') return
    if (!PhaseStartEventPayloadSchema.safeParse(body.data).success) {
      ctx.addIssue({
        code: 'custom',
        path: ['data'],
        message: 'a phase event here must be a valid start edge; done edges go through phase-complete'
      })
    }
    // Measured on the RAW body: the route stores `data` as sent, and the start
    // schema's own bound sees only its parsed output, with unknown keys gone.
    if (JSON.stringify(body.data ?? null).length > PHASE_EVENT_PAYLOAD_MAX_CHARS) {
      ctx.addIssue({
        code: 'custom',
        path: ['data'],
        message: `serialized payload exceeds ${PHASE_EVENT_PAYLOAD_MAX_CHARS} characters`
      })
    }
  })
export type RecordAgentRunEventRequest = z.infer<typeof RecordAgentRunEventRequestSchema>

export const RecordAgentRunEventResponseSchema = z.object({
  message: z.string(),
  data: AgentRunSchema
})
export type RecordAgentRunEventResponse = z.infer<typeof RecordAgentRunEventResponseSchema>

// POST /internal/agent-runs/:publicId/resume
//
// THE REQUEST SHAPE IS THE LIVE ONE; the response below is not. U2 shipped this
// route as a 501 stub, and SCH-1720 (plan U4) replaced it with the real
// transition — the response a caller gets today is
// {@link ResumeAgentRunResultResponseSchema} at the bottom of this section.
// {@link ResumeAgentRunResponseSchema} is kept only so a client pinned to the
// stub's shape still parses; nothing in this repo returns it.

export const ResumeAgentRunRequestSchema = z.object({
  /** The human's or event's follow-up content, when there is one. */
  followUp: z.string().optional()
})
export type ResumeAgentRunRequest = z.infer<typeof ResumeAgentRunRequestSchema>

/** U2's 501 shape. No longer returned by any route — see the note above. */
export const ResumeAgentRunResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    status: z.literal('not_implemented')
  })
})
export type ResumeAgentRunResponse = z.infer<typeof ResumeAgentRunResponseSchema>

// =============================================================================
// Run-token endpoints
// =============================================================================
//
// THE VM PULLS; NOTHING IS PUSHED TO IT. exe.dev's `/exec` has no stdin, so
// anything handed to a VM would have to ride in a command body that the
// transport logs and that any `ls` on the lobby can see. Only the run id and
// the run token cross that channel (KTD2); the VM then fetches its brief, its
// agent environment and its routing over HTTP with that token, and reports
// back the same way. Every contract below is authed by the PER-RUN REPORT
// TOKEN — the one credential a VM holds that is scoped to itself — not by the
// service key.
//
// Customer Pulse (SCH-2095) is what still calls them: the backend factory
// control loop these were first written for was retired in SCH-3202.

// POST /internal/agent-runs/:publicId/heartbeat

/**
 * The 60-second liveness post from the VM runner (KTD14).
 *
 * IT CARRIES USAGE, and that is not incidental: `costUsd` has to be current on
 * the ROW for the tick to enforce a budget at all (R15, R16), and a run that
 * reported its cost only at the end could overspend by its entire remaining
 * budget before anyone noticed. The runner enforces the same ceiling locally,
 * so a lost heartbeat costs at most one interval of overspend rather than the
 * whole run.
 *
 * `agentOutputAt` IS A SEPARATE CLOCK FROM THE HEARTBEAT, deliberately. The
 * heartbeat says the VM is alive; this says the SESSION is producing. A hung
 * session heartbeats forever, and without this field the only timeout that
 * could catch it is the lane's maximum run time — hours later.
 */
export const AgentRunHeartbeatRequestSchema = z.object({
  /** ISO-8601 time of the last stream-json event the runner saw. */
  agentOutputAt: z.string().datetime().optional(),
  /** Cumulative session cost so far, in dollars. */
  costUsd: z.number().nonnegative().optional(),
  inputTokens: z.number().int().nonnegative().optional(),
  outputTokens: z.number().int().nonnegative().optional(),
  /** The Claude session id, so a resume can continue it in place (KTD6). */
  sessionId: z.string().min(1).max(64).optional()
})
export type AgentRunHeartbeatRequest = z.infer<typeof AgentRunHeartbeatRequestSchema>

/**
 * The heartbeat's REPLY IS A CONTROL CHANNEL, not an acknowledgement.
 *
 * It is the only moment the control plane can reach a running VM without
 * opening a connection to it, so a cancel, a kill switch flip, or a budget
 * breach travels back on it. `stop` tells the runner to end the session and
 * exit — a zombie VM that keeps working while a retry starts on a new one is
 * the failure this prevents, and it is why the runner also exits on a 409 or a
 * long run of heartbeat failures rather than trusting that it will be told.
 */
export const AgentRunHeartbeatResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    /** The runner ends the session when this is true. */
    stop: z.boolean(),
    /** Why, when `stop` is true — surfaced in the runner's own log. */
    stopReason: z.enum(['canceled', 'budget', 'mode-off', 'superseded']).nullable(),
    /** The ceiling the runner enforces locally, echoed so it cannot drift from the row. */
    budgetUsd: z.number().nullable()
  })
})
export type AgentRunHeartbeatResponse = z.infer<typeof AgentRunHeartbeatResponseSchema>

// POST /internal/agent-runs/:publicId/usage

/**
 * The FINAL cost report, posted once as the session ends (R16).
 *
 * SEPARATE FROM THE HEARTBEAT BECAUSE ITS GUARD IS THE OPPOSITE ONE. A
 * heartbeat is only meaningful while the run is `RUNNING` — a beat from a run
 * the tick already killed must change nothing. Usage is a FACT about work
 * already done, and the moment it matters most is exactly the moment that
 * guard would refuse it: the runner posts its totals as the session ends,
 * often after a stall or budget kill has already written `FAILED`. A run whose
 * cost is invisible because it failed is a run nobody can account for.
 *
 * Every field is optional and every one is written as a HIGH-WATER MARK by
 * `recordUsage`, so a reordered delivery cannot walk a total backwards past a
 * budget the tick has already seen crossed.
 */
export const AgentRunUsageRequestSchema = z.object({
  /** Cumulative session cost so far, in dollars. */
  costUsd: z.number().nonnegative().optional(),
  inputTokens: z.number().int().nonnegative().optional(),
  outputTokens: z.number().int().nonnegative().optional()
})
export type AgentRunUsageRequest = z.infer<typeof AgentRunUsageRequestSchema>

export const AgentRunUsageResponseSchema = z.object({
  message: z.string(),
  data: AgentRunSchema
})
export type AgentRunUsageResponse = z.infer<typeof AgentRunUsageResponseSchema>

// GET /internal/agent-runs/:publicId/brief

/** A work branch the runner pushed: `fix/<publicId>` or `feat/<publicId>` (the backend checks the id). */
const WorkBranchSchema = z.string().regex(/^(fix|feat)\/[A-Za-z0-9_-]+$/, 'must be fix/<run id> or feat/<run id>')

/** A full 40-character lowercase commit sha. */
const CommitShaSchema = z.string().regex(/^[0-9a-f]{40}$/, 'must be a 40-character lowercase commit sha')

/**
 * The phase runner's per-lane caps, as the brief serves them (KTD9): the lane's
 * `factory.phases` entry plus the run-level correction cap, with every agent
 * phase's caps resolved. PRESENT ONLY FOR A LANE THE PHASE RUNNER SERVES — its
 * absence is what keeps `vm-run.ts` on the single-session path.
 *
 * Every cap has a schema default, so a locally authored `--brief <file>` may
 * leave them out; `laneVersion` has none, for the reason its config field
 * gives. It is the lane version the CONFIG ROW was seeded for, distinct from
 * `resume.laneVersion`, the version the RUN was dispatched under.
 */
export const AgentRunBriefPhasesSchema = z.object({
  laneVersion: z.string().min(1).max(64),
  stopBeforePhase: PhaseNameSchema.nullish(),
  /**
   * The per-run stop (factory operator plan, KTD11): the runner runs step
   * indexes up to and including this one, then stops before the next. On the
   * file sink that ends the run with the `range-end` outcome; on the ledger
   * sink it parks with `configured-stop`, the per-run form of
   * `stopBeforePhase`. Set by the launcher (`--to`), never by the config row,
   * and never carried over to a resume: it must be given again.
   */
  stopAfterStep: z.number().int().min(0).nullish(),
  /** Run-level correction cap across every phase of this attempt (KTD13). */
  maxCorrections: z.number().int().min(0).default(2),
  phases: z.partialRecord(AgentPhaseNameSchema, FactoryPhaseCapsSchema).default({})
})
export type AgentRunBriefPhases = z.infer<typeof AgentRunBriefPhasesSchema>

/**
 * Where a resumed or retried run picks up (KTD7), from the four resume columns
 * plus the accepted envelopes replayed, in step order, from the run's phase
 * `done` events. Every column is null on a run that has not started a step.
 */
export const AgentRunBriefResumeSchema = z
  .object({
    workBranch: WorkBranchSchema.nullable(),
    /** The tip the runner last pushed; `HEAD` must equal it before any step runs. */
    workBranchSha: CommitShaSchema.nullable(),
    /** Index of the next step to execute, already clamped to `build` when no work branch exists. */
    phaseCursor: z.number().int().min(0).nullable(),
    /** The `LANE_VERSION` the run was dispatched under. */
    laneVersion: z.string().min(1).max(64).nullable(),
    acceptedEnvelopes: z
      .array(AcceptedEnvelopeSchema)
      .default([])
      .refine(envelopes => envelopes.every((e, i) => i === 0 || e.stepIndex > (envelopes[i - 1]?.stepIndex ?? -1)), {
        message: 'accepted envelopes are replayed in strictly increasing step order'
      }),
    /**
     * What the run has already spent (`AgentRun.cost_usd`), or null before any
     * spend was recorded. The runner seeds its cumulative cost with it: the
     * column is a `GREATEST` high-water mark (KTD23), so a resumed runner that
     * counted from zero would report less than the row already holds and every
     * later phase's spend would be lost to it. Optional, so a brief from an
     * older backend still parses.
     */
    costUsd: z.number().nonnegative().nullish(),
    /** The run's recorded token totals, for the same reason: both columns are high-water marks too. */
    inputTokens: z.number().int().nonnegative().nullish(),
    outputTokens: z.number().int().nonnegative().nullish()
  })
  // Each pair is written together (KTD7), so half of one is a corrupt row, and
  // serving it would let a resume skip the drift check the missing half feeds.
  .refine(resume => (resume.workBranch === null) === (resume.workBranchSha === null), {
    message: 'workBranch and workBranchSha are both set or both null',
    path: ['workBranchSha']
  })
  .refine(resume => (resume.phaseCursor === null) === (resume.laneVersion === null), {
    message: 'phaseCursor and laneVersion are both set or both null',
    path: ['laneVersion']
  })
  // The branch is pushed by a commit step, long after the bootstrap wrote the cursor.
  .refine(resume => resume.workBranch === null || resume.phaseCursor !== null, {
    message: 'a work branch implies a phase cursor and lane version',
    path: ['phaseCursor']
  })
  // Only steps BEFORE the cursor have completed; an envelope at or past it, or
  // any envelope on a run with no cursor, is not accepted output to replay.
  .refine(
    resume =>
      resume.phaseCursor === null
        ? resume.acceptedEnvelopes.length === 0
        : resume.acceptedEnvelopes.every(envelope => envelope.stepIndex < (resume.phaseCursor ?? 0)),
    { message: 'accepted envelopes must come from steps before phaseCursor', path: ['acceptedEnvelopes'] }
  )
export type AgentRunBriefResume = z.infer<typeof AgentRunBriefResumeSchema>

/**
 * Everything the VM needs to run the lane, fetched once at start (R9).
 *
 * `priorBranch` / `priorPrUrl` ARE WHAT STOP A RETRY OPENING A SECOND PR. A
 * retry reuses the same run row (KTD4) and lands on a fresh VM that has none of
 * the previous attempt's context, so the branch and PR it must continue have to
 * arrive in the brief; without them the lane would branch from `dev` again and
 * open a second PR for one ticket.
 *
 * `resumeContext` is the same argument for a park: the question that parked the
 * run and the allowed human's answer, so a resumed session knows what it is
 * answering.
 */
export const AgentRunBriefResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    runId: z.string(),
    lane: AgentRunLaneSchema,
    /** The seven-part brief, as markdown. */
    brief: z.string(),
    /**
     * Which runner and model this lane and phase get (R11). The entry itself
     * is the lane's `lead`, which `vm-run.ts` launches on the single-session
     * path and still validates on the runner path. `phases` is the per-agent-
     * phase map the phase runner reads — additive, and absent for a lane it
     * does not serve.
     */
    routing: FactoryRoutingEntrySchema.extend({
      phases: z.partialRecord(AgentPhaseNameSchema, FactoryRoutingEntrySchema).nullish()
    }),
    budgetUsd: z.number().nullable(),
    timeouts: z.object({
      stallMs: z.number().int(),
      progressMs: z.number().int(),
      maxRunMs: z.number().int()
    }),
    /** 1 on the first attempt; 2+ means the fields below are populated. */
    attempt: z.number().int().min(1),
    priorBranch: z.string().nullable(),
    priorPrUrl: z.string().nullable(),
    /**
     * The run's Linear ticket key (`SCH-1234`), or null for a run with no
     * ticket. Additive and optional, so a brief from an older backend still
     * parses; the phase runner's `pr` step titles its PR with it.
     */
    linearIssueKey: z.string().nullish(),
    /** Present only when this start is a resume of a parked run. */
    resumeContext: z
      .object({
        parkQuestion: z.string(),
        /** The allowed resumer's answer, fenced as data by the caller (KTD19). */
        followUp: z.string(),
        /** Set when the parked VM survived and the session can be continued in place. */
        sessionId: z.string().nullable()
      })
      .nullable(),
    /** Phase runner caps for this lane; absent means the single-session path (KTD9). */
    phases: AgentRunBriefPhasesSchema.nullish(),
    /** Phase runner resume position; absent on the single-session path (KTD7). */
    resume: AgentRunBriefResumeSchema.nullish(),
    /**
     * Marks a SEEDED brief (factory operator plan, KTD11): a new file-sink run
     * started from a saved run's state — a resume from its report, or a step
     * slice. It is the only brief whose `resume` block a file-sink run
     * accepts, and the ledger sink refuses it: a ledger run is re-entered only
     * through the resume route. The backend never serves it.
     */
    seed: z
      .object({
        /** The run the state was copied from; that run is never written to. */
        fromRunId: z.string().min(1).max(128)
      })
      .nullish()
  })
})
export type AgentRunBriefResponse = z.infer<typeof AgentRunBriefResponseSchema>

// GET /internal/agent-runs/:publicId/agent-env

/**
 * The agent environment, from AWS Secrets Manager rather than a file on
 * somebody's Mac (R10).
 *
 * GATED ON `RUNNING`, and the gate is narrow on purpose (KTD3). It stops a
 * stale token from fetching secrets AGAIN — it does not revoke what was already
 * delivered, which stays valid until rotated. That is why the VM deletes
 * `~/.agent-env` and `~/.agent-run.env` when it parks or ends, rather than
 * relying on this gate: a VM parked for 24 hours must not sit on credentials at
 * rest. A resume re-fetches with a new token.
 *
 * Phase 1 serves the whole `agents/env` set, as VMs hold today; least-privilege
 * per lane is deferred follow-up work.
 */
export const AgentRunAgentEnvResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    /** Key-value pairs, written by the VM to a mode-600 file and never logged. */
    env: z.record(z.string(), z.string())
  })
})
export type AgentRunAgentEnvResponse = z.infer<typeof AgentRunAgentEnvResponseSchema>

// POST /internal/agent-runs/:publicId/cancel
//
// Service-key, not run-token: a run must not be able to cancel itself, and the
// callers are the tick (label removed, issue canceled, `factory.mode = off`)
// and an operator. The response is the run, so the caller sees
// `cancelRequestedAt` set — the cancel is RECORDED here and ACTED ON by the
// tick, which still has to tear the VM down. A cancelled run is never retried.

export const CancelAgentRunRequestSchema = z.object({
  /** Operator-facing reason, stored as the run's last error. */
  reason: z.string().min(1).max(500).optional()
})
export type CancelAgentRunRequest = z.infer<typeof CancelAgentRunRequestSchema>

export const CancelAgentRunResponseSchema = z.object({
  message: z.string(),
  data: AgentRunSchema
})
export type CancelAgentRunResponse = z.infer<typeof CancelAgentRunResponseSchema>

/**
 * The real resume response (R17), replacing U2's 501 stub above once U4 lands.
 *
 * A RESUMED RUN GOES BACK THROUGH THE QUEUE (KTD6) — `PARKED -> QUEUED`, or
 * `PARKED -> ASSESSING` when the park happened mid-assessment. It needs a slot
 * like any other run, so `status` is what the caller gets rather than a
 * promise that anything started.
 *
 * The existing {@link ResumeAgentRunRequestSchema} is reused unchanged: its
 * `followUp` is exactly the allowed human's answer (R23), and only comments
 * from `factory.gates.resumers` ever become one (KTD19).
 */
export const ResumeAgentRunResultResponseSchema = z.object({
  message: z.string(),
  data: AgentRunSchema
})
export type ResumeAgentRunResultResponse = z.infer<typeof ResumeAgentRunResultResponseSchema>

// =============================================================================
// Super-admin read surface (SCH-1219)
// =============================================================================
//
// A SECOND SET OF CONTRACTS, NOT A REPLACEMENT. Everything above is the
// internal service-key API (`/internal/agent-runs`) that `run-ledger`, the VM
// executors and `sj runs` already call in production; those shapes are frozen.
// The three below serve `/v1/agent-runs`, the Clerk-JWT super-admin router the
// web dashboard reads. Both routers run over ONE service layer
// (`services/agent-runs/`), so the rows are identical — what differs is the
// query surface (the admin table facets on several values at once) and
// pagination (the admin list returns a cursor; the internal list never did, and
// adding one there would change a shipped response shape for no caller).
//
// READ-ONLY BY CONSTRUCTION. There is deliberately no admin create / claim /
// resume / event-write contract here. Resuming a PARKED run from a browser is
// its own decision with its own auth implications — not a side effect of two
// routers sharing a service layer.

/**
 * GET /v1/agent-runs — request contract.
 *
 * FILTERS ACCEPT ONE VALUE OR MANY, and that is the whole reason this schema
 * exists rather than reusing {@link ListAgentRunsRequestSchema}. The admin
 * table's faceted filters are multi-select, so the browser sends
 * `?status=PARKED&status=BLOCKED`, which Express's query parser hands over as an
 * array. The internal contract is single-valued and `.strict()`; widening it
 * would change the shape a shipped CLI and three executors already parse.
 *
 * Not `.strict()`, matching `ListOperationsEventsRequestSchema`: a table that
 * appends a stray query param should drop it, not 400 the page.
 */
export const AdminListAgentRunsRequestSchema = z.object({
  status: z.union([AgentRunStatusSchema, z.array(AgentRunStatusSchema)]).optional(),
  lane: z.union([AgentRunLaneSchema, z.array(AgentRunLaneSchema)]).optional(),
  source: z.union([AgentRunSourceSchema, z.array(AgentRunSourceSchema)]).optional(),
  trigger: z.union([AgentRunTriggerSchema, z.array(AgentRunTriggerSchema)]).optional(),
  mode: z.union([AgentRunModeSchema, z.array(AgentRunModeSchema)]).optional(),
  executor: z.union([AgentRunExecutorSchema, z.array(AgentRunExecutorSchema)]).optional(),
  /**
   * Only runs currently `PARKED` — the "waiting on a human" view, and the one
   * that matters most.
   *
   * ⚠️ SEND IT OR OMIT IT; NEVER SEND `parked=false`. `z.coerce.boolean()`
   * follows JavaScript truthiness, so the STRING `"false"` coerces to `true`
   * and a client that helpfully serializes an unchecked toggle gets the exact
   * opposite of what it asked for. Inherited verbatim from the internal
   * contract above rather than fixed here, because changing the coercion would
   * change how a shipped CLI flag parses; the filter is a shorthand toggle, and
   * the service only ever acts on `=== true`.
   */
  parked: z.coerce.boolean().optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
  /** Public id of the last run on the previous page. */
  cursor: z.string().min(1).optional()
})
export type AdminListAgentRunsRequest = z.infer<typeof AdminListAgentRunsRequestSchema>

/**
 * GET /v1/agent-runs — response.
 *
 * NEWEST FIRST, unlike the internal list's oldest-first. The internal list feeds
 * a poller draining a queue, which wants the oldest unclaimed run; a human
 * opening a table wants what just happened.
 *
 * There is no total count. Counting the table on every page load buys a number
 * the screen does not need, and a keyset cursor cannot honestly produce one
 * mid-stream anyway. `nextCursor === null` is how the client knows it is done.
 *
 * Every column the admin table renders is on `AgentRunSchema` already, with one
 * exception worth stating: PR LINKS ARE NOT A FIELD. They live inside the
 * `outcomes` JSON that a run fills in as its work lands (`outcomes` also carries
 * the Linear objects touched, the CI result, and the review-findings count).
 * The client reads them from there; adding a derived top-level column would
 * freeze a shape the runs themselves are still deciding.
 */
export const AdminListAgentRunsResponseSchema = z.object({
  message: z.string(),
  data: z.array(AgentRunSchema),
  /** Null when the stream is exhausted. */
  nextCursor: z.string().nullable()
})
export type AdminListAgentRunsResponse = z.infer<typeof AdminListAgentRunsResponseSchema>

// GET /v1/agent-runs/:publicId
//
// Deliberately NO separate response contract: a single run is the same row over
// both routers, so both return `GetAgentRunResponse` above. A parallel
// `AdminGetAgentRunResponseSchema` would be a second name for one shape and the
// first place the two surfaces could silently disagree.

/**
 * GET /v1/agent-runs/:publicId/events — request contract.
 *
 * The run's lifecycle trail. Its events are `OperationsEvent` rows
 * (`domain: 'agent'`, `eventClass: 'agent_run'`) joined by
 * `correlationId = <the run's public id>` — there is no child table.
 *
 * THIS IS A SECOND FETCH ON PURPOSE, not a field on the run. The trail keeps
 * growing after the row that opened the detail dialog was listed, so a client
 * that rendered it from the cached list row would show a stale run as finished.
 */
export const AdminGetAgentRunEventsRequestSchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).optional(),
  /** Public id of the last event on the previous page. */
  cursor: z.string().min(1).optional()
})
export type AdminGetAgentRunEventsRequest = z.infer<typeof AdminGetAgentRunEventsRequestSchema>

/**
 * GET /v1/agent-runs/:publicId/events — response.
 *
 * Newest first, matching every other timeline read in the repo
 * (`listOperationsEvents`, `listMobileAppEvents`). The rows are the shared
 * `OperationsEventSchema`, not an agent-run-specific projection: the trail IS
 * operations-event rows, and a private copy of that shape would be a second
 * place for the closed-union columns to drift.
 *
 * An unknown run id is a 404, not an empty trail — "this run has no events yet"
 * and "no such run" are different answers and the dialog renders them
 * differently.
 */
export const AdminGetAgentRunEventsResponseSchema = z.object({
  message: z.string(),
  data: z.array(OperationsEventSchema),
  /** Null when the stream is exhausted. */
  nextCursor: z.string().nullable()
})
export type AdminGetAgentRunEventsResponse = z.infer<typeof AdminGetAgentRunEventsResponseSchema>

// GET /internal/agent-runs/:publicId/events
//
// The same trail, read with the service key instead of a Clerk session
// (SCH-1861). `sj runs <id>` and any other headless caller live behind
// `/internal` and may not present a browser session.

/**
 * GET /internal/agent-runs/:publicId/events — request contract.
 *
 * A SECOND REQUEST SCHEMA, BUT NO SECOND RESPONSE SCHEMA. The answer is
 * {@link AdminGetAgentRunEventsResponseSchema}, because both routers read
 * `services/agent-runs/reads.ts` and that file's rule is that the envelope is
 * the thing that must not drift — two independently-authed routers answering
 * the same question have to answer it identically. What genuinely differs is
 * the CEILING: the dashboard pages a dialog and takes 200, while the headless
 * callers here ask for the last handful of events (`sj runs` asks for 20) from
 * a VM over the public internet, so a smaller cap is the right ask-size and
 * makes an accidental full-trail pull a 400 rather than a large page.
 */
export const ListAgentRunEventsRequestSchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional(),
  /** Public id of the last event on the previous page. */
  cursor: z.string().min(1).optional()
})
export type ListAgentRunEventsRequest = z.infer<typeof ListAgentRunEventsRequestSchema>
